import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, AfterViewChecked, OnInit } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { debounceTime, map } from 'rxjs/operators';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { Select } from '@ngxs/store';
import { UnitState } from 'app/state/unit.state';
import { Unit } from '@appbolaget/aware-model';
import { Observable } from 'rxjs';
import { MatOptionModule } from '@angular/material/core';
import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatInputModule } from '@angular/material/input';
import { Autocompletable } from '@helpers/interfaces';
import { ModelWithTitleAndImage } from '@components/form-components';

@Component({
    selector: 'aw-autocomplete',
    templateUrl: 'autocomplete.component.html',
    styleUrls: ['./autocomplete.scss'],
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        MatProgressBarModule,
        NgFor,
        MatOptionModule,
        NgIf,
        AsyncPipe,
    ],
})
export class AutoCompleteComponent implements OnInit, AfterViewChecked {
    _value: any = '';

    previousValue: any;

    @Select(UnitState.activeUnit) activeUnit$: Observable<Unit>;
    @ViewChild('textInput') textInput: ElementRef;
    @ViewChild('auto') autocomplete: MatAutocomplete;

    @Input() excludes: Array<any>;
    @Input() placeholder = 'Sök';
    @Input() service: Autocompletable;
    @Input() filter: any = {};
    @Input() options: any = {};
    @Input() method = 'autocomplete';
    @Input() displayProperty = 'title';
    @Input() appearance: MatFormFieldAppearance = 'fill';
    @Input() optionLabelPredicate?: (option: ModelWithTitleAndImage) => string;

    @Input('value')
    set value(value) {
        this._value = value;

        if (value) {
            this.setValue(value);
        } else {
        }
    }

    get value() {
        return this._value;
    }

    @Output() onSelect = new EventEmitter();

    IS_LOADING = false;
    IS_SELECTED = false;
    HAS_EXACT_MATCH = false;
    CAN_CREATE_NEW: boolean;
    HAS_FOCUSED: boolean;

    autocompleteControl: UntypedFormControl = new UntypedFormControl();

    searchOptions: any[] = [];
    filterString = '';

    get floatPlaceholder() {
        return this.value ? 'always' : 'auto';
    }

    constructor(protected cdRef: ChangeDetectorRef) {}

    ngAfterViewChecked() {
        this.cdRef.detectChanges();
    }

    ngOnInit() {
        this.autocompleteControl.valueChanges
            .pipe(
                debounceTime(500),
                map((object) => this.displayFn(object)),
            )
            .subscribe((value) => {
                if (value && value === this.previousValue) {
                    return;
                }

                if (this.IS_SELECTED) {
                    this.IS_SELECTED = false;
                    return;
                }

                this.previousValue = value;

                this.search(value).subscribe((data) => {
                    this.searchOptions = data;
                });
            });
    }

    displayFn(obj: any): string {
        return obj && typeof obj === 'object' ? obj[this.displayProperty] : obj;
    }

    filterResult(result: any[]): any[] {
        return result.filter((option) => option[this.displayProperty].toLowerCase().indexOf(this.filterString.toLowerCase()) === 0);
    }

    onFocus() {
        if (!this.HAS_FOCUSED) {
            this.autocompleteControl.setValue(this.value);
            this.autocompleteControl.markAsTouched();

            setTimeout(() => {
                this.HAS_FOCUSED = true;
            }, 600);
        }
    }

    optionSelected($event) {
        if ($event.option.value) {
            this.IS_SELECTED = true;

            this.onSelect.emit($event.option.value);
        }
    }

    search(value: string = '') {
        this.IS_LOADING = true;
        this.filterString = value ? value : '';

        return this.service[this.method]({ search: this.filterString, filter: this.filter }, this.options).pipe(
            map((res: any[]) => {
                this.IS_LOADING = false;

                if (this.excludes) {
                    res = res.filter((obj) => {
                        for (const exclude of this.excludes) {
                            if (exclude.uuid === obj.uuid) {
                                return false;
                            }
                        }

                        return true;
                    });
                }

                this.HAS_EXACT_MATCH = res.filter((obj) => obj[this.displayProperty].toLowerCase() === this.filterString.toLowerCase())
                    .length
                    ? true
                    : false;

                return res;
            }),
        );
    }

    setValue(value) {
        this.autocompleteControl.setValue(value[this.displayProperty]);
        this.autocompleteControl.markAsTouched();

        setTimeout(() => {
            this.textInput.nativeElement.value = value[this.displayProperty];
        });
    }
}
